import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShare,
  faPaste,
  faCopy,
  faCheck,
  faCircleNotch,
  faSms,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import { formatPhoneForDisplay } from "../../../../../utils/PhoneDisplayFormatter";
import HelpGuideButton from "../../../HelpGuideButton";
import "../../email/css/EmailCheckStart.css";

const FORWARD_NUMBER = "+1-888-899-5004";

const CopyNumberChip = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(FORWARD_NUMBER);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may not be available */
    }
  };

  return (
    <button
      type="button"
      className={`ecs-chip${copied ? " copied" : ""}`}
      onClick={handleCopy}
      aria-label={`${t("sms.mobile.copy")} ${FORWARD_NUMBER}`}
    >
      <span className="ecs-chip-address">{FORWARD_NUMBER}</span>
      <span className="ecs-chip-action" aria-live="polite">
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied ? t("sms.mobile.copied") : t("sms.mobile.copy")}
      </span>
    </button>
  );
};

/**
 * Entry point for checking a text message. Full variant is the first-run
 * screen (no history yet); compact variant sits above existing history.
 *
 * awaitState: "idle" | "waiting" | "timedOut"
 */
const SmsCheckStart = ({
  variant = "full",
  userPhone,
  awaitState = "idle",
  onStartAwaiting,
  onCancelAwaiting,
  onPasteInstead,
}) => {
  const { t } = useTranslation();
  const displayPhone = formatPhoneForDisplay(userPhone);

  const sendFromText = displayPhone
    ? t("sms.start.forward.step3", { phone: displayPhone })
    : t("sms.start.forward.step3Fallback");
  const timeoutText = displayPhone
    ? t("sms.start.timeout.body", {
        number: FORWARD_NUMBER,
        phone: displayPhone,
      })
    : t("sms.start.timeout.bodyFallback", { number: FORWARD_NUMBER });

  const renderAwaitStatus = (sentLabel) => {
    if (awaitState === "waiting") {
      return (
        <div className="ecs-status ecs-status--waiting" role="status">
          <FontAwesomeIcon icon={faCircleNotch} spin />
          <div>
            <strong>{t("sms.start.waiting.title")}</strong>
            <span>{t("sms.start.waiting.body")}</span>
          </div>
          <button
            type="button"
            className="ecs-link-btn"
            onClick={onCancelAwaiting}
          >
            {t("sms.start.waiting.cancel")}
          </button>
        </div>
      );
    }
    if (awaitState === "timedOut") {
      return (
        <div className="ecs-status ecs-status--timeout" role="status">
          <div>
            <strong>{t("sms.start.timeout.title")}</strong>
            <span>{timeoutText}</span>
          </div>
          <span className="ecs-status-actions">
            <button
              type="button"
              className="ecs-link-btn"
              onClick={onStartAwaiting}
            >
              {t("sms.start.timeout.retry")}
            </button>
            <button
              type="button"
              className="ecs-link-btn"
              onClick={onCancelAwaiting}
            >
              {t("sms.start.timeout.dismiss")}
            </button>
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={
          variant === "full"
            ? "common-button primary ecs-primary-btn"
            : "ecs-link-btn"
        }
        onClick={onStartAwaiting}
      >
        {sentLabel}
      </button>
    );
  };

  if (variant === "compact") {
    return (
      <div className="ecs-compact">
        <div className="ecs-compact-row">
          <FontAwesomeIcon icon={faSms} className="ecs-compact-icon" />
          <span>{t("sms.start.compact.forward")}</span>
          <CopyNumberChip />
          {awaitState === "idle" &&
            renderAwaitStatus(t("sms.start.compact.sentBtn"))}
          <span className="ecs-compact-or">{t("sms.start.compact.or")}</span>
          <button
            type="button"
            className="ecs-link-btn"
            onClick={onPasteInstead}
          >
            {t("sms.start.compact.paste")}
          </button>
        </div>
        {awaitState !== "idle" && renderAwaitStatus()}
      </div>
    );
  }

  return (
    <section className="ecs" aria-label={t("sms.page.title")}>
      <div className="ecs-options">
        <div className="ecs-option ecs-option--primary">
          <span className="ecs-badge">{t("sms.start.recommended")}</span>
          <h2 className="ecs-option-title">
            <FontAwesomeIcon icon={faShare} />
            {t("sms.start.forward.title")}
          </h2>
          <p className="ecs-option-why">{t("sms.start.forward.why")}</p>

          <ol className="ecs-steps">
            <li>{t("sms.start.forward.step1")}</li>
            <li>
              {t("sms.start.forward.step2")}
              <CopyNumberChip />
            </li>
            <li>
              <strong>{sendFromText}</strong>
              <span className="ecs-step-detail">
                {t("sms.start.forward.step3Detail")}
              </span>
            </li>
          </ol>

          <div className="ecs-help">
            <span>{t("sms.start.forward.helpLabel")}</span>
            <HelpGuideButton guideId={2} />
          </div>

          <div className="ecs-actions">
            {renderAwaitStatus(t("sms.start.forward.sentBtn"))}
          </div>
        </div>

        <div className="ecs-option">
          <h2 className="ecs-option-title">
            <FontAwesomeIcon icon={faPaste} />
            {t("sms.start.paste.title")}
          </h2>
          <p className="ecs-option-why">{t("sms.start.paste.why")}</p>
          <div className="ecs-actions">
            <button
              type="button"
              className="common-button ecs-secondary-btn"
              onClick={onPasteInstead}
            >
              {t("sms.start.paste.btn")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmsCheckStart;
